import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Checkbox from "@mui/joy/Checkbox";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Sheet from "@mui/joy/Sheet";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Study, StudyId } from "../../../models/study";
import AlertDialogModal from "../../../shared/components/ui/AlertDialogModal";
import { formatAndCapitalizeString } from "../../../shared/utils/text";
import { getStudyCurrentState } from "../pages/StudiesPage";

interface StudyTableProps {
  readonly studies: Study[];
  readonly deleteStudies: (ids: StudyId[]) => void;
}

export default function StudyTable({ studies, deleteStudies }: StudyTableProps) {
  const navigate = useNavigate();

  // States
  const [selected, setSelected] = React.useState<StudyId[]>([]);
  const [openDeleteStudyModal, setOpenDeleteStudyModal] = React.useState(false);

  const toggleStudySelection = (studyId: StudyId, isChecked: boolean) => {
    setSelected((ids) =>
      isChecked ? ids.concat(studyId) : ids.filter((itemId) => itemId !== studyId)
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    setSelected(isChecked ? studies.map((row) => row.study_id) : []);
  };

  const handleStudyClick = (studyId: StudyId) => {
    navigate(`/study/${studyId}`);
  };

  const handleDeleteClick = () => {
    setOpenDeleteStudyModal(true);
  };

  const getPrincipalInvestigator = (study: Study) => {
    return study.associated_parties.filter((x) => x.role === "principal-investigator")[0]?.name;
  };

  return (
    <>
      <AlertDialogModal
        alertMessage="Are you sure you want to delete the selected studies?"
        open={openDeleteStudyModal}
        setOpen={setOpenDeleteStudyModal}
        handleDelete={() => deleteStudies(selected)}
      />
      <Sheet variant="outlined" sx={{ borderRadius: "sm" }}>
        <Table
          aria-labelledby="studyTable"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground": "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 48, textAlign: "center", padding: "12px 6px" }}>
                <Checkbox
                  size="sm"
                  indeterminate={selected.length > 0 && selected.length !== studies.length}
                  checked={selected.length === studies.length}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  color={
                    selected.length > 0 || selected.length === studies.length
                      ? "primary"
                      : undefined
                  }
                  sx={{ verticalAlign: "text-bottom" }}
                />
              </th>
              <th style={{ width: 100, padding: "12px 6px" }}>Acronym</th>
              <th style={{ padding: "12px 6px" }}>Title</th>
              <th style={{ width: 290, padding: "12px 6px" }}>Current state</th>
              <th style={{ width: 200, padding: "12px 6px" }}>Principal investigator</th>
              <th style={{ width: "52px" }}>
                {selected.length > 0 && (
                  <IconButton onClick={handleDeleteClick} color="danger" variant="outlined">
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {studies.map((study) => (
              <tr
                key={study.study_id}
                onClick={() => handleStudyClick(study.study_id)}
                style={{ cursor: "pointer" }}
              >
                <td
                  style={{ textAlign: "center", width: 120, verticalAlign: "middle" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    size="sm"
                    checked={selected.includes(study.study_id)}
                    color={selected.includes(study.study_id) ? "primary" : undefined}
                    onChange={(event) => toggleStudySelection(study.study_id, event.target.checked)}
                    slotProps={{ checkbox: { sx: { textAlign: "left" } } }}
                    sx={{ verticalAlign: "text-bottom" }}
                  />
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Typography>{study.acronym}</Typography>
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Typography>{study.title}</Typography>
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Chip>{formatAndCapitalizeString(getStudyCurrentState(study).state)}</Chip>
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Typography>{getPrincipalInvestigator(study)}</Typography>
                </td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
    </>
  );
}
